import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthPageShell } from '@/features/auth/components/auth-page-shell'
import { normalizeReturnUrl } from '@/features/auth/guards'
import { registerWithPassword } from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { buildDeviceFingerprint } from '@/features/auth/utils/device-fingerprint'

type RegisterSearch = {
  redirect?: string
}

export const Route = createFileRoute('/register')({
  validateSearch: (search: Record<string, unknown>): RegisterSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: RegisterRoute,
})

function RegisterRoute() {
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()
  const setSession = useAuthSessionStore((state) => state.setSession)
  const setInitialized = useAuthSessionStore((state) => state.setInitialized)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const target = normalizeReturnUrl(redirect)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!name.trim() || !email.trim() || !password) {
      toast.error('请完整填写注册信息')
      return
    }

    if (password !== confirmPassword) {
      toast.error('两次输入的密码不一致')
      return
    }

    setIsSubmitting(true)

    try {
      const session = await registerWithPassword({
        name: name.trim(),
        email: email.trim(),
        password,
        deviceFingerprint: await buildDeviceFingerprint(),
      })
      setSession(session)
      setInitialized(true)
      toast.success('注册成功，已为你自动登录')
      await navigate({ to: target })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '注册失败，请稍后重试')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthPageShell
      title="注册快剪账号"
      description="本站可以单独注册和登录，但账户、会员和支付能力仍与主站保持统一。"
      footerText="已经有账号？"
      footerActionLabel="去登录"
      footerActionTo="/login"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <Label htmlFor="name">昵称</Label>
          <Input
            id="name"
            autoComplete="nickname"
            placeholder="请输入昵称"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">邮箱</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">密码</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            placeholder="至少 8 位密码"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">确认密码</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            placeholder="再次输入密码"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
          {isSubmitting ? '注册中...' : '注册并进入工作台'}
        </Button>
      </form>
    </AuthPageShell>
  )
}